import { ref, shallowRef } from 'vue'
import { formatBridgeDateOfServiceForUsState } from '@gh/common/utils'
import { logClinicalCalendarConsultantFilter } from '@/utils/logClinicalCalendarConsultantFilter'
import { bridgeCallables } from '../bridgeCallables'
import type { ConsultationConsultType } from '@gh/common/models/consultation/consultation'
import { readBridgeServiceTypeIdForConsultType } from '../bridgeConfig'

export type BookableProviderEligibility = {
  consultantId: string
  tellescopeUserId: string
  bridgeProviderId: string
}

type BookableProviderEligibilityRequest = {
  dateOfService: string
  state: string
  serviceTypeId: string
}

type PrefetchScope = {
  customerId?: string
  policyId?: string | null
}

const PREFETCH_MAX_AGE_MS = 5 * 60 * 1000

let pendingPrefetch: {
  key: string
  startedAt: number
  promise: Promise<{ providers: BookableProviderEligibility[] }>
} | null = null

export function resetBookableProviderEligibilityPrefetch(): void {
  pendingPrefetch = null
}

/** `dateOfService` carries milliseconds, so key on its calendar day or a prefetch never matches. */
function requestKey(request: BookableProviderEligibilityRequest): string {
  return `${request.state}|${request.serviceTypeId}|${request.dateOfService.slice(0, 10)}`
}

function prefetchKey(request: BookableProviderEligibilityRequest, scope: PrefetchScope): string {
  const policySegment =
    scope.policyId === undefined ? '' : scope.policyId === null ? '__null__' : scope.policyId
  return `${scope.customerId ?? ''}|${policySegment}|${requestKey(request)}`
}

function buildRequest(args: {
  state: string
  consultType: ConsultationConsultType
}): BookableProviderEligibilityRequest | null {
  const serviceTypeId = readBridgeServiceTypeIdForConsultType(args.consultType)
  if (!serviceTypeId) {
    return null
  }
  return {
    dateOfService: formatBridgeDateOfServiceForUsState(new Date(), args.state),
    state: args.state,
    serviceTypeId,
  }
}

/**
 * Starts the PE gateway call before the slot picker mounts. The next matching
 * `loadForInsuredVisit` consumes it once; failures fall back to a fresh call.
 */
export function prefetchBookableProviderEligibility(args: {
  state: string
  consultType: ConsultationConsultType
  customerId?: string
  policyId?: string | null
}): void {
  const request = buildRequest(args)
  if (!request) {
    return
  }
  const key = prefetchKey(request, args)
  if (
    pendingPrefetch?.key === key &&
    Date.now() - pendingPrefetch.startedAt < PREFETCH_MAX_AGE_MS
  ) {
    return
  }
  const promise = Promise.resolve().then(() =>
    bridgeCallables.listBookableProviderEligibility(request),
  )
  const entry = { key, startedAt: Date.now(), promise }
  pendingPrefetch = entry
  promise.catch(() => {
    if (pendingPrefetch === entry) {
      pendingPrefetch = null
    }
  })
}

function takePrefetch(
  request: BookableProviderEligibilityRequest,
  scope: PrefetchScope,
): Promise<{ providers: BookableProviderEligibility[] }> | null {
  const key = prefetchKey(request, scope)
  const entry = pendingPrefetch
  if (!entry || entry.key !== key) {
    return null
  }
  pendingPrefetch = null
  if (Date.now() - entry.startedAt >= PREFETCH_MAX_AGE_MS) {
    return null
  }
  return entry.promise.catch(() => bridgeCallables.listBookableProviderEligibility(request))
}

/**
 * Insured calendar Soft PE gateway — holds mapped providers locally (not Pinia).
 * Empty mapped list is valid; gateway failure surfaces via `error` and leaves filtering off.
 */
export function useBookableProviderEligibilityCalendar() {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const mappedProviders = shallowRef<BookableProviderEligibility[]>([])
  /** `null` = PE filter not applied (self-pay, not loaded, or gateway error). */
  const mappedTellescopeUserIds = ref<Set<string> | null>(null)
  let currentLoadId = 0

  async function loadForInsuredVisit(args: {
    state: string
    consultType: ConsultationConsultType
    customerId?: string
    policyId?: string | null
  }): Promise<void> {
    const request = buildRequest(args)
    if (!request) {
      const msg = 'Bridge service type is not configured'
      error.value = msg
      mappedProviders.value = []
      mappedTellescopeUserIds.value = null
      logClinicalCalendarConsultantFilter('pe-gateway-error', {
        visitState: args.state,
        reason: 'missing-service-type-id',
        filterMode: 'off-passthrough',
      })
      throw new Error(msg)
    }

    const loadId = ++currentLoadId
    loading.value = true
    error.value = null
    const { dateOfService, serviceTypeId } = request
    try {
      const result =
        (await takePrefetch(request, args)) ??
        (await bridgeCallables.listBookableProviderEligibility(request))
      if (loadId !== currentLoadId) return
      mappedProviders.value = result.providers
      mappedTellescopeUserIds.value =
        result.providers.length > 0
          ? new Set(result.providers.map((provider) => provider.tellescopeUserId))
          : null
      logClinicalCalendarConsultantFilter('pe-gateway-load', {
        visitState: args.state,
        dateOfService,
        serviceTypeId,
        providerCount: result.providers.length,
        filterMode: result.providers.length > 0 ? 'intersect' : 'passthrough-empty-mapped',
        providers: result.providers.map((provider) => ({
          consultantId: provider.consultantId,
          tellescopeUserId: provider.tellescopeUserId,
          bridgeProviderId: provider.bridgeProviderId,
        })),
      })
    } catch (cause) {
      if (loadId !== currentLoadId) return
      mappedProviders.value = []
      mappedTellescopeUserIds.value = null
      error.value = cause instanceof Error ? cause.message : 'Provider eligibility check failed'
      logClinicalCalendarConsultantFilter('pe-gateway-error', {
        visitState: args.state,
        dateOfService,
        serviceTypeId,
        error: error.value,
        filterMode: 'off-passthrough',
      })
      throw cause
    } finally {
      if (loadId === currentLoadId) {
        loading.value = false
      }
    }
  }

  function clear() {
    mappedProviders.value = []
    mappedTellescopeUserIds.value = null
    error.value = null
    loading.value = false
    logClinicalCalendarConsultantFilter('pe-gateway-clear', {
      filterMode: 'off',
    })
  }

  return {
    loading,
    error,
    mappedProviders,
    mappedTellescopeUserIds,
    loadForInsuredVisit,
    clear,
  }
}
